import { CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { formatIsoDate } from '../lib/format';
import { useNow } from '../lib/use-now';
import { Popover, usePopover } from './Popover';
import styles from './DatePicker.module.css';

// Ours, never `<input type="date">`: the stock control follows the browser's locale
// ("mm/dd/yyyy" on a US machine) where every other date here is DD-MM-YYYY (06-qol §11),
// and ignores our palette entirely (CLAUDE.md §5). Adapted from Tomo's DatePicker, onto
// our anchored Popover and tokens.
//
// The value is a plain calendar date, `YYYY-MM-DD`, with no instant behind it — which
// timezone the day belongs to is the caller's business (see `dhakaToIso`).

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'] as const;
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'] as const;
const GRID_DAYS = 42;

interface CalendarDay {
  readonly year: number;
  readonly month: number;
  readonly day: number;
}

const pad = (value: number): string => String(value).padStart(2, '0');

function toValue(date: CalendarDay): string {
  return `${date.year}-${pad(date.month + 1)}-${pad(date.day)}`;
}

function parseValue(value: string): CalendarDay | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;
  return { year: Number(match[1]), month: Number(match[2]) - 1, day: Number(match[3]) };
}

/** Today in the given zone, as a calendar day — "today" in Dhaka, not in the browser's zone. */
function todayIn(timeZone: string, now: number): CalendarDay {
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(now);
  return parseValue(parts) ?? { year: 2000, month: 0, day: 1 };
}

/** The 6-row grid, from the Sunday on or before the 1st — the Sun–Thu week reads left to right. */
function gridFor(year: number, month: number): readonly CalendarDay[] {
  const first = new Date(Date.UTC(year, month, 1));
  const start = Date.UTC(year, month, 1 - first.getUTCDay());
  return Array.from({ length: GRID_DAYS }, (_, index) => {
    const date = new Date(start + index * 86_400_000);
    return { year: date.getUTCFullYear(), month: date.getUTCMonth(), day: date.getUTCDate() };
  });
}

interface DatePickerProps {
  readonly value: string;
  readonly onChange: (value: string) => void;
  /** Days before this `YYYY-MM-DD` cannot be picked. */
  readonly min?: string;
  /** Days after this `YYYY-MM-DD` cannot be picked. */
  readonly max?: string;
  readonly id?: string;
  readonly label: string;
  readonly placeholder?: string;
  readonly isInvalid?: boolean;
  readonly disabled?: boolean;
  readonly describedBy?: string;
  /** Whose "today" the grid marks. */
  readonly timeZone?: string;
}

export function DatePicker({
  value,
  onChange,
  min,
  max,
  id,
  label,
  placeholder = 'Pick a date',
  isInvalid = false,
  disabled = false,
  describedBy,
  timeZone = 'Asia/Dhaka',
}: DatePickerProps): ReactNode {
  const { anchor, triggerRef, toggle, close } = usePopover();
  const selected = parseValue(value);
  const today = todayIn(timeZone, useNow());
  const [view, setView] = useState<{ year: number; month: number }>(() => selected ?? today);

  const open = (): void => {
    // Re-centre on the chosen month each time it opens.
    setView(parseValue(value) ?? today);
    toggle();
  };
  const step = (by: number): void => {
    const next = new Date(Date.UTC(view.year, view.month + by, 1));
    setView({ year: next.getUTCFullYear(), month: next.getUTCMonth() });
  };
  const isOutside = (dayValue: string): boolean => (min !== undefined && dayValue < min) || (max !== undefined && dayValue > max);
  const pick = (day: CalendarDay): void => {
    onChange(toValue(day));
    close();
  };

  return (
    <>
      <button
        ref={triggerRef}
        id={id}
        type="button"
        className={`${styles.trigger} ${isInvalid ? styles.invalid : ''}`}
        onClick={open}
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={anchor !== null}
        aria-describedby={describedBy}
        aria-invalid={isInvalid || undefined}
      >
        <span className={`${styles.value} ${selected ? '' : styles.placeholder}`}>{selected ? formatIsoDate(value) : placeholder}</span>
        <CalendarDays size={15} className={styles.icon} aria-hidden />
      </button>

      <Popover anchor={anchor} onClose={close} label={label}>
        <div className={styles.calendar}>
          <div className={styles.head}>
            <button type="button" className={styles.nav} aria-label="Previous month" onClick={() => step(-1)}>
              <ChevronLeft size={16} />
            </button>
            <span className={styles.title}>
              {MONTHS[view.month]} {view.year}
            </span>
            <button type="button" className={styles.nav} aria-label="Next month" onClick={() => step(1)}>
              <ChevronRight size={16} />
            </button>
          </div>
          <div className={styles.weekdays} aria-hidden>
            {WEEKDAYS.map((weekday) => (
              <span key={weekday}>{weekday}</span>
            ))}
          </div>
          <div className={styles.grid}>
            {gridFor(view.year, view.month).map((day) => {
              const dayValue = toValue(day);
              const isSelected = dayValue === value;
              const isToday = dayValue === toValue(today);
              const classes = [styles.day, day.month !== view.month ? styles.muted : '', isToday ? styles.today : '', isSelected ? styles.selected : '']
                .filter(Boolean)
                .join(' ');
              return (
                <button
                  key={dayValue}
                  type="button"
                  className={classes}
                  disabled={isOutside(dayValue)}
                  aria-label={formatIsoDate(dayValue)}
                  aria-pressed={isSelected}
                  aria-current={isToday ? 'date' : undefined}
                  onClick={() => pick(day)}
                >
                  {day.day}
                </button>
              );
            })}
          </div>
          <div className={styles.foot}>
            <button type="button" className={styles.todayButton} disabled={isOutside(toValue(today))} onClick={() => pick(today)}>
              Today
            </button>
          </div>
        </div>
      </Popover>
    </>
  );
}
